import { createStore } from 'vuex';
import axios from 'axios';
import { cleanUserRequirementsResponse } from '@/utils/textFormatting';

// Cache keys identify a (imageUrl, language, modelQuality) triple so that
// switching language or model invalidates a section's cached result. Using
// a JSON-stringified array avoids ambiguity if any component contains '|'.
export function buildSectionCacheKey({ imageUrl, language, modelQuality }) {
  return JSON.stringify([imageUrl || null, language || null, modelQuality || null]);
}

export function buildRequirementsCacheKey({ imageUrl, language, userIntent }) {
  const intentSignature = JSON.stringify([
    userIntent?.expectations || '',
    userIntent?.purpose || ''
  ]);
  return JSON.stringify([imageUrl || null, language || null, intentSignature]);
}

export default createStore({
  state: {
    analysis: {
      analysisId: null,
      analysis: null,
      imageUrl: null,
      personalizedTeaserImageUrl: null,
    },
    analysisRooms: null,
    analysisCost: null,
    studioStarterPack: null,
    modelQuality: 'fast',
    userHadPremiumWhenUploaded: false,
    lastAnalyzedImage: null,
    userIntent: null,
    evaluation: null,
    analysisSectionStatus: {
      rooms: 'idle',
      costs: 'idle',
      requirements: 'idle',
      studioStarterPack: 'idle',
    },
    analysisSectionCacheKey: {
      rooms: null,
      costs: null,
      requirements: null,
      studioStarterPack: null,
    },
  },
  mutations: {
    setAnalysis(state, { analysis, imageUrl, personalizedTeaserImageUrl = null, analysisId = null }) {
      state.analysis = {
        analysisId,
        analysis,
        imageUrl,
        personalizedTeaserImageUrl,
      }
    },
    setAnalysisId(state, analysisId) {
      state.analysis.analysisId = analysisId
    },
    setAnalysisRooms(state, rooms) {
      state.analysisRooms = rooms
    },
    setAnalysisCost(state, cost) {
      state.analysisCost = cost
    },
    setStudioStarterPack(state, pack) {
      state.studioStarterPack = pack
    },
    setModelQuality(state, quality) {
      state.modelQuality = quality
    },
    resetAnalysisData(state) {
      state.analysis = {
        analysisId: null,
        analysis: null,
        imageUrl: null,
        personalizedTeaserImageUrl: null,
      };
      state.analysisRooms = null;
      state.analysisCost = null;
      state.studioStarterPack = null;
      state.lastAnalyzedImage = null;
      state.userIntent = null;
      state.evaluation = null;
      state.analysisSectionStatus = {
        rooms: 'idle',
        costs: 'idle',
        requirements: 'idle',
        studioStarterPack: 'idle',
      };
      state.analysisSectionCacheKey = {
        rooms: null,
        costs: null,
        requirements: null,
        studioStarterPack: null,
      };
    },
    resetState(state) {
      state.analysis = {
        analysisId: null,
        analysis: null,
        imageUrl: null,
        personalizedTeaserImageUrl: null,
      };
      state.analysisRooms = null;
      state.analysisCost = null;
      state.studioStarterPack = null;
      state.modelQuality = 'fast';
      state.userHadPremiumWhenUploaded = false;
      state.analysisSectionStatus = {
        rooms: 'idle',
        costs: 'idle',
        requirements: 'idle',
        studioStarterPack: 'idle',
      };
      state.analysisSectionCacheKey = {
        rooms: null,
        costs: null,
        requirements: null,
        studioStarterPack: null,
      };
    },
    setUserHadPremiumWhenUploaded(state, hadPremium) {
      state.userHadPremiumWhenUploaded = hadPremium;
    },
    setLastAnalyzedImage(state, url) {
      state.lastAnalyzedImage = url
    },
    setUserIntent(state, intent) {
      state.userIntent = intent
    },
    setEvaluation(state, evaluation) {
      state.evaluation = cleanUserRequirementsResponse(evaluation)
    },
    setAnalysisSectionStatus(state, { section, status }) {
      if (!state.analysisSectionStatus[section]) {
        state.analysisSectionStatus[section] = 'idle';
      }
      state.analysisSectionStatus[section] = status;
    },
    setAnalysisSectionCacheKey(state, { section, key }) {
      state.analysisSectionCacheKey[section] = key;
    },
    setAnalysisSectionCacheKeys(state, keys = {}) {
      state.analysisSectionCacheKey = {
        ...state.analysisSectionCacheKey,
        ...keys,
      };
    }
  },
  actions: {
    async preloadRoomAnalysis({ commit, state }, { imageUrl, language, authHeader } = {}) {
      const cacheKey = buildSectionCacheKey({ imageUrl, language, modelQuality: state.modelQuality })
      if (state.analysisRooms && state.analysisSectionCacheKey.rooms === cacheKey) {
        commit('setAnalysisSectionStatus', { section: 'rooms', status: 'ready' })
        return
      }

      try {
        commit('setAnalysisSectionStatus', { section: 'rooms', status: 'loading' })
        const response = await axios.post('/api/analyze-barrier-free', {
          imageUrl,
          language,
          model: state.modelQuality
        }, {
          headers: authHeader ? { Authorization: authHeader } : {},
        })

        if (response.data?.data) {
          commit('setAnalysisRooms', response.data.data)
          commit('setLastAnalyzedImage', imageUrl)
          commit('setAnalysisSectionCacheKey', { section: 'rooms', key: cacheKey })
          commit('setAnalysisSectionStatus', { section: 'rooms', status: 'ready' })
        }
      } catch (error) {
        commit('setAnalysisSectionStatus', { section: 'rooms', status: 'error' })
        console.error('Failed to preload room analysis:', error)
      }
    },
    async preloadCostAnalysis({ commit, state }, { imageUrl, language, authHeader } = {}) {
      const cacheKey = buildSectionCacheKey({ imageUrl, language, modelQuality: state.modelQuality })
      if (state.analysisCost && state.analysisSectionCacheKey.costs === cacheKey) {
        commit('setAnalysisSectionStatus', { section: 'costs', status: 'ready' })
        return
      }

      try {
        commit('setAnalysisSectionStatus', { section: 'costs', status: 'loading' })
        const response = await axios.post('/api/analyze-cost', {
          imageUrl,
          language,
          model: state.modelQuality
        }, {
          headers: authHeader ? { Authorization: authHeader } : {},
        })

        if (response.data?.data) {
          commit('setAnalysisCost', response.data.data)
          commit('setLastAnalyzedImage', imageUrl)
          commit('setAnalysisSectionCacheKey', { section: 'costs', key: cacheKey })
          commit('setAnalysisSectionStatus', { section: 'costs', status: 'ready' })
        }
      } catch (error) {
        commit('setAnalysisSectionStatus', { section: 'costs', status: 'error' })
        console.error('Failed to preload cost analysis:', error)
      }
    },
    async preloadUserEvaluation({ commit, state }, { analysis, imageUrl, userIntent, language, authHeader } = {}) {
      if (!userIntent?.expectations?.trim()) {
        commit('setEvaluation', null)
        commit('setAnalysisSectionStatus', { section: 'requirements', status: 'idle' })
        return
      }

      // Cache key includes language + a hash of userIntent so that
      // changing the language or the requirements text re-fetches the
      // evaluation. modelQuality is irrelevant — user_query does not
      // route through the model selector.
      const cacheKey = buildRequirementsCacheKey({ imageUrl, language, userIntent })
      if (state.evaluation && state.analysisSectionCacheKey.requirements === cacheKey) {
        commit('setAnalysisSectionStatus', { section: 'requirements', status: 'ready' })
        return
      }

      try {
        commit('setAnalysisSectionStatus', { section: 'requirements', status: 'loading' })
        const response = await axios.post('/api/user_query', {
          userIntent,
          analysis,
          imageUrl: imageUrl || null,
          language,
        }, {
          headers: authHeader ? { Authorization: authHeader } : {},
        })

        if (response.data?.success) {
          commit('setEvaluation', response.data.evaluation)
          commit('setAnalysisSectionCacheKey', { section: 'requirements', key: cacheKey })
          commit('setAnalysisSectionStatus', { section: 'requirements', status: 'ready' })
          return
        }

        throw new Error('Failed to preload requirements evaluation')
      } catch (error) {
        commit('setAnalysisSectionStatus', { section: 'requirements', status: 'error' })
        console.error('Failed to preload user evaluation:', error)
      }
    },
    // Studio "starter pack": derivation paragraph + 3-6 tailored
    // change-command cards in one AI call. Reuses the requirements cache
    // key shape so an unchanged goal stays cached on the analysis doc and
    // any edit to expectations/purpose invalidates correctly.
    async preloadStudioStarterPack({ commit, state }, { analysisId, imageUrl, userIntent, language, authHeader } = {}) {
      if (!userIntent?.expectations?.trim() && !userIntent?.purpose?.trim()) {
        commit('setStudioStarterPack', null)
        commit('setAnalysisSectionStatus', { section: 'studioStarterPack', status: 'idle' })
        return
      }
      if (!imageUrl) {
        commit('setAnalysisSectionStatus', { section: 'studioStarterPack', status: 'idle' })
        return
      }

      const cacheKey = buildRequirementsCacheKey({ imageUrl, language, userIntent })
      if (state.studioStarterPack && state.analysisSectionCacheKey.studioStarterPack === cacheKey) {
        commit('setAnalysisSectionStatus', { section: 'studioStarterPack', status: 'ready' })
        return
      }

      try {
        commit('setAnalysisSectionStatus', { section: 'studioStarterPack', status: 'loading' })
        const response = await axios.post('/api/studio/starter-pack', {
          analysisId: analysisId || null,
          imageUrl,
          userIntent,
          language,
        }, {
          headers: authHeader ? { Authorization: authHeader } : {},
        })

        const pack = response.data?.data
        if (response.data?.success && pack) {
          commit('setStudioStarterPack', { derivation: pack.derivation, suggestions: pack.suggestions })
          commit('setAnalysisSectionCacheKey', { section: 'studioStarterPack', key: cacheKey })
          commit('setAnalysisSectionStatus', { section: 'studioStarterPack', status: 'ready' })
          return
        }
        throw new Error('Failed to preload studio starter pack')
      } catch (error) {
        commit('setAnalysisSectionStatus', { section: 'studioStarterPack', status: 'error' })
        console.error('Failed to preload studio starter pack:', error)
      }
    },
  }
})
